import React, { useState, useEffect, useRef } from 'react';
import {
	Container,
	Typography,
	Button,
	CircularProgress,
	TextField,
	Autocomplete,
	Chip,
	Box,
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import { toast } from 'react-toastify';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate, useParams } from 'react-router-dom';
import type { FormData } from '../../../types';
import ProductBasicInfo from './ProductBasicInfo';
import ProductDescription from './ProductDescription';
import ProductCategory from './ProductCategory';
import ProductOptions from './ProductOptions';
import WholesaleFields from './WholesaleFields';
import BulkDiscountFields from './BulkDiscountFields';
import ProductImages from './ProductImages';
import { getSinlgeProduct, updateProduct } from '../../../services/api/ProductAPI';
import ColorVariants from './ColorVariants';

const initialFormData: FormData = {
	_id: '',
	productId: '',
	name: '',
	category: '',
	description: '',
	price: '',
	quantity: '',
	bestSelling: false,
	trending: false,
	productImage: [],
	weight: '',
	weightUnit: 'kg',
	isInquiry: false,
	isBulkDiscount: false,
	isCustomized: false,
	slug: '',
	isWholesale: false,
	wholesaleFields: [],
	buldDiscountsData: [],
	wholesaleMinQuantity: 1,
	isStickoutPrice: false,
	isColorVariantAvailable: false,
	strickoutPrice: 1,
	colorVariants: [],
	imageAltTags: [],
};

const EditProductPage = () => {
	const [formData, setFormData] = useState<FormData>(initialFormData);
	const [loading, setLoading] = useState(false);
	const quillRef = useRef<any>(null);
	const { product_id } = useParams();
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');
	const [fetchError, setFetchError] = useState<string | null>(null);
	const navigate = useNavigate();
	const [countdown, setCountdown] = useState(10);
	const [productID, setProductID] = useState<string | undefined>(undefined);
	const [seo, setSeo] = useState<any>({
		metaTitle: '',
		metaDescription: '',
		canonicalTag: '',
		keywords: [],
	});

	useEffect(() => {
		const fetchProduct = async () => {
			try {
				if (product_id) {
					setLoading(true);
					const response = await getSinlgeProduct(product_id);
					if (response && response.data && response.data.product) {
						const product = response.data.product;
						const transformedColorVariants =
							product.colorVariants?.map((variant: any) => ({
								colorName: variant.colorName || '',
								colorCode: variant.colorCode || '#ffffff',
								price: variant.price || 0,
								strickoutPrice: variant.strickoutPrice || 0,
								sizes: variant.sizes || [],
								colorImage: variant.colorImage || [],
								_id: variant._id, // Keep the original ID if needed
								imageAltTag: variant.imageAltTag,
							})) || [];
						console.log(transformedColorVariants, 'transformedColorVariants');

						setProductID(product?._id ? product?._id : undefined);
						const transformedData: FormData = {
							_id: product._id || '',
							productId: product.productId || '',
							name: product.name || '',
							category: product.category || '',
							description: product.description || '',
							price: product.price || 1,
							quantity: product.quantity?.toString() || '',
							bestSelling: product.bestSelling || false,
							trending: product.trending || false,
							productImage: product.image || [],
							weight: product.weight?.toString() || '',
							weightUnit: product.weightUnit || 'kg',
							isInquiry: product.isInquiry || false,
							isBulkDiscount: product.isBulkDiscount || false,
							isCustomized: product.isCustomized || false,
							slug: product.slug || '',
							isWholesale: product.isWholesale || false,
							wholesaleFields:
								product.wholesaleFields?.map((field: any) => ({
									type: field.type as 'text' | 'image',
									label: field.label || '',
									required: field.required || false,
								})) || [],
							buldDiscountsData:
								product.bulkDiscounts?.map((discount: any) => ({
									minQuantity: discount.minQuantity || 1,
									maxQuantity: discount.maxQuantity || 1,
									discountPercentage: discount.discountPercentage || 1,
								})) || [],
							wholesaleMinQuantity: product.wholesaleMinQuantity || 1,
							isStickoutPrice: product.isstickoutPrice || false,
							isColorVariantAvailable: product.isColorVariantAvailable || false,
							strickoutPrice: product.strickoutPrice || product.strickoutPrice || 1,
							colorVariants: transformedColorVariants,
							imageAltTags: product.imageAltTag,
						};
						console.log(transformedData, 'transformedData');
						console.log(product, 'product');
						setSeo({
							metaTitle: product?.metaTitle,
							metaDescription: product?.metaDescription,
							canonicalTag: product?.canonicalTag,
							keywords: product?.keywords,
						});
						setFormData(transformedData);
					} else {
						setFetchError('Product data not found in response');
					}
				}
			} catch (error) {
				console.error('Failed to fetch product:', error);
				setFetchError('Failed to load product data');
			} finally {
				setLoading(false);
			}
		};

		fetchProduct();
	}, [product_id]);

	useEffect(() => {
		if (fetchError) {
			const timer = setInterval(() => {
				setCountdown((prev) => {
					if (prev <= 1) {
						clearInterval(timer);
						navigate('/product');
						return 0;
					}
					return prev - 1;
				});
			}, 1000);

			return () => clearInterval(timer);
		}
	}, [fetchError, navigate]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		e.stopPropagation();
		if (!formData.isColorVariantAvailable && formData.productImage.length === 0) {
			toast.error('Please upload at least one image.');
			return;
		}
		if (!productID) {
			console.log('product is not found');
			return;
		}
		const formDataToSend = new FormData();

		formDataToSend.append('name', formData.name);
		formDataToSend.append('slug', formData.slug);
		formDataToSend.append('description', formData.description);
		formDataToSend.append('category', formData.category);
		formDataToSend.append('trending', String(formData.trending));
		formDataToSend.append('metaTitle', String(seo.metaTitle));
		formDataToSend.append('metaDescription', String(seo.metaDescription));
		formDataToSend.append('canonicalTag', String(seo.canonicalTag));

		seo.keywords.forEach((text: any, index: any) => {
			formDataToSend.append(`keywords[${index}]`, text);
		});
		formData.productImage.forEach((file) => {
			formDataToSend.append('productImage', file);
		});

		if (!formData.isInquiry) {
			formDataToSend.append('weight', formData.weight);
			if (!formData.isColorVariantAvailable) {
				formDataToSend.append('price', formData.price.toString());
			}
			formDataToSend.append('quantity', formData.quantity);
			formDataToSend.append('bestSelling', String(formData.bestSelling));
			if (formData.isWholesale) {
				formDataToSend.append(
					'wholesaleMinQuantity',
					String(formData.wholesaleMinQuantity)
				);
			}
			formDataToSend.append('weightUnit', formData.weightUnit);
			formDataToSend.append('isBulkDiscount', String(formData.isBulkDiscount));
			formDataToSend.append('isCustomized', String(formData.isCustomized));
			formDataToSend.append('isWholesale', String(formData.isWholesale));
		}

		formDataToSend.append('isInquiry', String(formData.isInquiry));
		formDataToSend.append('isColorVariantAvailable', String(formData.isColorVariantAvailable));
		formDataToSend.append('isStickoutPrice', String(formData.isStickoutPrice));
		if (formData.isStickoutPrice) {
			formDataToSend.append('strickoutPrice', String(formData.strickoutPrice));
		}

		formData.wholesaleFields.forEach((field, index) => {
			formDataToSend.append(`wholesaleFields[${index}][type]`, field.type);
			formDataToSend.append(`wholesaleFields[${index}][label]`, field.label);
			formDataToSend.append(`wholesaleFields[${index}][required]`, String(field.required));
		});

		formData.buldDiscountsData.forEach((discount, index) => {
			formDataToSend.append(
				`bulkDiscounts[${index}][minQuantity]`,
				String(discount.minQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][maxQuantity]`,
				String(discount.maxQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][discountPercentage]`,
				String(discount.discountPercentage)
			);
		});

		formData.imageAltTags.forEach((tag: any) => {
			formDataToSend.append('imageAltTag', tag);
		});

		formData.colorVariants.forEach((variant, index) => {
			formDataToSend.append(`colorVariants[${index}][colorName]`, variant.colorName);
			formDataToSend.append(
				`colorVariants[${index}][colorCode]`,
				variant.colorCode ? variant.colorCode : '#c62020'
			);
			formDataToSend.append(`colorVariants[${index}][price]`, String(variant.price));
			formDataToSend.append(
				`colorVariants[${index}][strickoutPrice]`,
				String(variant.strickoutPrice)
			);
			variant.sizes.forEach((size, index2) => {
				formDataToSend.append(`colorVariants[${index}][sizes][${index2}][size]`, size.size);
			});

			if (variant.imageAltTag && variant.imageAltTag.length > 0) {
				variant.imageAltTag.forEach((image: any) => {
					formDataToSend.append(`colorVariants[${index}][imageAltTag]`, image);
				});
			}
			if (variant.colorImage && variant.colorImage.length > 0) {
				variant.colorImage.forEach((image) => {
					if (image.file) {
						formDataToSend.append(`colorVariants[${index}][colorImage]`, image.file);
					}
				});
			}
		});

		try {
			setLoading(true);
			const response = await updateProduct(formDataToSend, productID);
			if (response.isError) {
				toast.error(response.data.errors);
			} else {
				toast.success(response.message || 'Product updated successfully!');
				resetForm();
				navigate(location.includes(slug as string) ? `/${slug}/product` : '/product');
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('Failed to update product.');
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData(initialFormData);
		setProductID(undefined);
		if (quillRef.current) {
			quillRef.current.getEditor().setContents([]);
		}
	};

	if (fetchError) {
		return (
			<div
				style={{
					height: '80vh',
					width: '100%',
					display: 'flex',
					flexDirection: 'column',
					alignItems: 'center',
					justifyContent: 'center',
					textAlign: 'center',
				}}
			>
				<Typography variant="h5" color="error" gutterBottom>
					{fetchError}
				</Typography>
				<Typography variant="body1">
					Redirecting to products page in {countdown} seconds...
				</Typography>
				<CircularProgress style={{ marginTop: 20 }} />
			</div>
		);
	}

	if (loading && !formData.name) {
		return (
			<div
				style={{
					height: '80vh',
					width: '100%',
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
				}}
			>
				<CircularProgress />
			</div>
		);
	}

	return (
		<div>
			<Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
				<Typography
					gutterBottom
					style={{ fontFamily: 'Poppins', fontWeight: '700', fontSize: '24px' }}
				>
					Edit Product
				</Typography>
				<Button
					variant="outlined"
					color="error"
					onClick={() => navigate('/product')}
					startIcon={<KeyboardBackspaceIcon />}
					disabled={loading}
				>
					Go Back
				</Button>
			</Grid>

			<Container>
				<form onSubmit={handleSubmit}>
					<Grid container spacing={2}>
						<ProductBasicInfo formData={formData} setFormData={setFormData} />

						<ProductDescription
							formData={formData}
							setFormData={setFormData}
							quillRef={quillRef}
						/>

						<ProductCategory formData={formData} setFormData={setFormData} />

						<ProductOptions formData={formData} setFormData={setFormData} />

						{!formData.isInquiry && (
							<>
								{formData.isWholesale && (
									<WholesaleFields
										formData={formData}
										setFormData={setFormData}
									/>
								)}

								{formData.isBulkDiscount && (
									<BulkDiscountFields
										formData={formData}
										setFormData={setFormData}
									/>
								)}
							</>
						)}

						{!formData.isColorVariantAvailable ? (
							<ProductImages formData={formData} setFormData={setFormData} />
						) : (
							<ColorVariants
								formData={formData}
								setFormData={setFormData}
								productId={product_id}
							/>
						)}

						<Box sx={{ pt: 2 }}>
							<strong>SEO</strong> <br />
							<Grid container spacing={2}>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box>
										<TextField
											size="small"
											label="MetaTitle"
											fullWidth
											value={seo.metaTitle}
											sx={{ fontFamily: 'Poppins' }}
											placeholder="MetaTitle"
											type="text"
											onChange={(e) =>
												setSeo({ ...seo, metaTitle: e.target.value })
											}
										/>
									</Box>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box>
										<TextField
											size="small"
											label="metaDescription"
											placeholder="metaDescription"
											value={seo.metaDescription}
											fullWidth
											sx={{ fontFamily: 'Poppins' }}
											type="text"
											onChange={(e) =>
												setSeo({ ...seo, metaDescription: e.target.value })
											}
										/>
									</Box>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box>
										<TextField
											size="small"
											label="canonicalTag"
											placeholder="canonicalTag"
											value={seo.canonicalTag}
											type="text"
											sx={{ fontFamily: 'Poppins' }}
											fullWidth
											onChange={(e) =>
												setSeo({ ...seo, canonicalTag: e.target.value })
											}
										/>
									</Box>
								</Grid>
								<Grid size={{ xs: 12, sm: 6 }}>
									<Box>
										<Autocomplete
											multiple
											freeSolo
											options={[]} // no predefined options; all input is accepted
											value={seo.keywords || []}
											onChange={(_, newValue) =>
												setSeo({
													...seo,
													keywords: newValue
														.map((v) => v.trim())
														.filter((v) => v !== ''),
												})
											}
											renderTags={(value, getTagProps) =>
												value.flatMap((option, index) => (
													<Box key={index}>
														<Chip
															variant="outlined"
															label={option}
															{...getTagProps({ index })}
															sx={{ fontFamily: 'Poppins' }}
														/>
													</Box>
												))
											}
											renderInput={(params) => (
												<TextField
													{...params}
													variant="outlined"
													size="small"
													label="Keywords"
													placeholder="Type and press Enter"
													sx={{ fontFamily: 'Poppins' }}
												/>
											)}
										/>
									</Box>
								</Grid>
							</Grid>
						</Box>

						<Grid size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={2}>
							<Button
								variant="outlined"
								color="error"
								onClick={() => navigate('/product')}
								disabled={loading}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								variant="contained"
								color="primary"
								disabled={loading}
							>
								{loading ? 'Loading...' : 'Update Product'}
							</Button>
						</Grid>
					</Grid>
				</form>
			</Container>
		</div>
	);
};

export default EditProductPage;
